import { Component } from '@contember/interface'
import {
	EditorElementTrigger,
	EditorGenericTrigger,
	EditorMarkTrigger,
	EditorReferenceTrigger,
	anchorElementType,
	boldMark,
	codeMark,
	createAlignHandler,
	headingElementType,
	highlightMark,
	italicMark,
	orderedListElementType,
	paragraphElementType,
	strikeThroughMark,
	underlineMark,
	unorderedListElementType,
} from '@contember/react-slate-editor'
import { BlockEditorField as BlockEditorFieldBase, BlockEditorFieldProps as BlockEditorFieldPropsBase, EditorBlock, EditorBlockToolbar, EditorInlineToolbar } from '@app/lib/editor'
import { Toggle } from '@app/lib/ui/toggle'
import { uic } from '@app/lib/utils'
import {
	AlignCenterIcon,
	AlignJustifyIcon,
	AlignLeftIcon,
	AlignRightIcon,
	BoldIcon,
	CodeIcon,
	FileDown,
	FileUp,
	Heading1Icon,
	Heading2Icon,
	Heading3Icon,
	HighlighterIcon,
	ItalicIcon,
	Link2Icon,
	ListIcon,
	ListOrderedIcon,
	PilcrowIcon,
	StrikethroughIcon,
	UnderlineIcon,
} from 'lucide-react'
import { InputField } from '@app/lib/form'
import { FileField } from '@app/components/blocks/articleSignUp'

export interface BlockEditorFieldProps {
	field: BlockEditorFieldPropsBase['field']
	referencesField: BlockEditorFieldPropsBase['referencesField']
	label?: string
}

const BlockButton = uic('button', {
	baseClass: 'bg-white p-2 inline-flex flex-col hover:bg-gray-100 border rounded-md w-32 items-center justify-center',
})

export const BlockEditorField = Component(({ field, referencesField, label }: BlockEditorFieldProps) => (
	<div className="mr-4 flex flex-col gap-2">
		{label && <label className="text-sm font-medium">{label}</label>}
		<BlockEditorFieldBase
			field={field}
			referencesField={referencesField}
			referenceDiscriminationField="type"
			plugins={
				[
					// Example how to register custom plugin
					// editor => {
					// 	editor.registerElement({
					// 		type: 'link',
					// 		isInline: true,
					// 		render: LinkElement,
					// 	})
					// },
				]
			}
		>
			{/* Example how to include toolbar */}
			<EditorBlockToolbar>
			{/* Example how to include references in toolbar */}
			{/* Quote reference example. See also line 213 */}
			{/*	<EditorReferenceTrigger referenceType="quote"><BlockButton><QuoteIcon /> Quote</BlockButton></EditorReferenceTrigger>*/}
			{/* Image reference example. See also line 218 */}
				<EditorReferenceTrigger referenceType="fileDownload"><BlockButton><FileDown /> File Download</BlockButton></EditorReferenceTrigger>

			{/* Table elements */}
			{/*	<EditorElementTrigger elementType={tableElementType}>*/}
			{/*		<BlockButton><TableIcon /> Table</BlockButton>*/}
			{/*	</EditorElementTrigger>*/}

			{/* Scroll target elements */}
			{/*	<EditorElementTrigger elementType={scrollTargetElementType}>*/}
			{/*		<BlockButton><LocateIcon /> Scroll target</BlockButton>*/}
			{/*	</EditorElementTrigger>*/}

			{/* Horizontal rule elements */}
			{/*	<EditorElementTrigger elementType={horizontalRuleElementType}>*/}
			{/*		<BlockButton><MinusIcon /> Horizontal rule</BlockButton>*/}
			{/*	</EditorElementTrigger>*/}
			</EditorBlockToolbar>
			<EditorInlineToolbar>
				<div>
					<EditorMarkTrigger mark={boldMark}>
						<Toggle>
							<BoldIcon className="h-3 w-3" />
						</Toggle>
					</EditorMarkTrigger>
					<EditorMarkTrigger mark={italicMark}>
						<Toggle>
							<ItalicIcon className="h-3 w-3" />
						</Toggle>
					</EditorMarkTrigger>
					<EditorMarkTrigger mark={underlineMark}>
						<Toggle>
							<UnderlineIcon className="h-3 w-3" />
						</Toggle>
					</EditorMarkTrigger>
					<EditorMarkTrigger mark={strikeThroughMark}>
						<Toggle>
							<StrikethroughIcon className="h-3 w-3" />
						</Toggle>
					</EditorMarkTrigger>
					<EditorMarkTrigger mark={highlightMark}>
						<Toggle>
							<HighlighterIcon className="h-3 w-3" />
						</Toggle>
					</EditorMarkTrigger>
					<EditorMarkTrigger mark={codeMark}>
						<Toggle>
							<CodeIcon className="h-3 w-3" />
						</Toggle>
					</EditorMarkTrigger>
					<EditorElementTrigger elementType={anchorElementType}>
						<Toggle>
							<Link2Icon className="h-3 w-3" />
						</Toggle>
					</EditorElementTrigger>
					{/*<Popover>*/}
					{/*	<PopoverTrigger asChild>*/}
					{/*		<Toggle><LinkIcon className="h-3 w-3" /></Toggle>*/}
					{/*	</PopoverTrigger>*/}
					{/*	<PopoverContent>*/}
					{/*		<EditorInlineReferencePortal referenceType="link">*/}
					{/*			<LinkField field="link" />*/}
					{/*			<ConfirmReferenceButton />*/}
					{/*		</EditorInlineReferencePortal>*/}
					{/*	</PopoverContent>*/}
					{/*</Popover>*/}
				</div>
				<div>
					<EditorElementTrigger elementType={paragraphElementType} suchThat={{ isNumbered: false }}>
						<Toggle>
							<PilcrowIcon className="h-3 w-3" />
						</Toggle>
					</EditorElementTrigger>
					<EditorElementTrigger elementType={headingElementType} suchThat={{ level: 1, isNumbered: false }}>
						<Toggle>
							<Heading1Icon className="h-3 w-3" />
						</Toggle>
					</EditorElementTrigger>
					<EditorElementTrigger elementType={headingElementType} suchThat={{ level: 2, isNumbered: false }}>
						<Toggle>
							<Heading2Icon className="h-3 w-3" />
						</Toggle>
					</EditorElementTrigger>
					<EditorElementTrigger elementType={headingElementType} suchThat={{ level: 3, isNumbered: false }}>
						<Toggle>
							<Heading3Icon className="h-3 w-3" />
						</Toggle>
					</EditorElementTrigger>
					<EditorElementTrigger elementType={unorderedListElementType}>
						<Toggle>
							<ListIcon className="h-3 w-3" />
						</Toggle>
					</EditorElementTrigger>
					<EditorElementTrigger elementType={orderedListElementType}>
						<Toggle>
							<ListOrderedIcon className="h-3 w-3" />
						</Toggle>
					</EditorElementTrigger>

					<EditorGenericTrigger {...createAlignHandler('start')}>
						<Toggle className="ml-4">
							<AlignLeftIcon className="h-3 w-3" />
						</Toggle>
					</EditorGenericTrigger>
					<EditorGenericTrigger {...createAlignHandler('end')}>
						<Toggle>
							<AlignRightIcon className="h-3 w-3" />
						</Toggle>
					</EditorGenericTrigger>
					<EditorGenericTrigger {...createAlignHandler('center')}>
						<Toggle>
							<AlignCenterIcon className="h-3 w-3" />
						</Toggle>
					</EditorGenericTrigger>
					<EditorGenericTrigger {...createAlignHandler('justify')}>
						<Toggle>
							<AlignJustifyIcon className="h-3 w-3" />
						</Toggle>
					</EditorGenericTrigger>
				</div>
			</EditorInlineToolbar>

			{/* Example how to include references in editor */}
			{/* Uncomment also line 71 */}
			{/* <EditorBlock name="fileDownload" label="File Download">
				<EditorBlockContent />
			</EditorBlock> */}

			{/* Uncomment also line 73 */}
			<EditorBlock name="fileDownload" label="File download">
			<InputField field="file.title" label="Title" />
			<FileField baseField="file" label="Document" />
			</EditorBlock>
		</BlockEditorFieldBase>
		<div></div>
	</div>
))
